import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/TranslationContext';
import { getAdminProducts, getAdminPickupLocations, getAdminOrders } from '../../services/api';
import './DashboardHome.css';

const DashboardHome: React.FC = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    products: 0,
    pickupLocations: 0,
    orders: 0,
    pendingOrders: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!token) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch data from API
        const [products, pickupLocations, orders] = await Promise.all([
          getAdminProducts(token),
          getAdminPickupLocations(token),
          getAdminOrders(token),
        ]);

        // Calculate stats
        // Flatten the orders object into an array
        const allOrders = Object.values(orders).flat();
        const pendingOrders = allOrders.filter(order => order.status === 'pending').length;

        // Update state
        setStats({
          products: products.length,
          pickupLocations: pickupLocations.length,
          orders: allOrders.length,
          pendingOrders,
        });
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token]);

  if (loading) {
    return <div className="dashboard-loading">{t('admin.common.loading')}</div>;
  }

  if (error) {
    return <div className="dashboard-error">{t('admin.common.error')}</div>;
  }

  return (
    <div className="dashboard-home">
      <h1>{t('admin.dashboardHome.title')}</h1>
      
      <div className="stats-container">
        <div className="stat-card">
          <h3>{t('admin.dashboard.nav.products')}</h3>
          <p className="stat-value">{stats.products}</p>
          <p className="stat-description">{t('admin.dashboardHome.totalProducts')}</p>
        </div>
        
        <div className="stat-card">
          <h3>{t('admin.dashboard.nav.pickupLocations')}</h3>
          <p className="stat-value">{stats.pickupLocations}</p>
          <p className="stat-description">{t('admin.dashboardHome.totalPickupLocations')}</p>
        </div>
        
        <div className="stat-card">
          <h3>{t('admin.dashboard.nav.orders')}</h3>
          <p className="stat-value">{stats.orders}</p>
          <p className="stat-description">{t('admin.dashboardHome.totalOrders')}</p>
        </div>
        
        <div className="stat-card">
          <h3>{t('admin.orders.statuses.pending')}</h3>
          <p className="stat-value">{stats.pendingOrders}</p>
          <p className="stat-description">{t('admin.orders.statuses.pending')}</p>
        </div>
      </div>
      
      <div className="dashboard-actions">
        <h2>{t('admin.dashboardHome.summary')}</h2>
        <div className="action-buttons">
          <a href="/admin/products/new" className="action-button">
            {t('admin.products.addProduct')}
          </a>
          <a href="/admin/pickup-locations/new" className="action-button">
            {t('admin.pickupLocations.addLocation')}
          </a>
          <a href="/admin/orders" className="action-button">
            {t('admin.dashboardHome.viewAll')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
